// 094 -- Admin Order detail + status timeline + status-change form.

import { useEffect, useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import type { OrderPublic, OrderStatus } from "@dugads/shared";
import * as api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Breadcrumbs } from "@/components/breadcrumbs";

function fmtMoney(cents: number, currency: string): string {
  return `${currency === "ZAR" ? "R " : `${currency} `}${(cents / 100).toFixed(2)}`;
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-ZA", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const STATUS_BADGE: Record<OrderStatus, string> = {
  submitted: "bg-yellow-100 text-yellow-800",
  acknowledged: "bg-blue-100 text-blue-800",
  fulfilled: "bg-green-100 text-green-800",
  cancelled: "bg-muted text-muted-foreground",
  failed: "bg-red-100 text-red-800",
};

const TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  submitted: ["acknowledged", "cancelled", "failed"],
  acknowledged: ["fulfilled", "cancelled", "failed"],
  fulfilled: [],
  cancelled: [],
  failed: ["submitted"],
};

export default function AdminShopOrderDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [order, setOrder] = useState<OrderPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toStatus, setToStatus] = useState<OrderStatus | "">("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  function load() {
    if (!slug) return;
    setLoading(true);
    api.getAdminOrder(slug)
      .then((r) => setOrder(r.order))
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slug]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!order || toStatus === "") return;
    setSaving(true);
    setError(null);
    try {
      const r = await api.patchOrderStatus(order.slug, {
        to_status: toStatus,
        reason: reason || undefined,
      });
      setOrder(r.order);
      setToStatus("");
      setReason("");
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to update status");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="text-muted-foreground" data-page="admin-shop-order-detail" data-state="loading">Loading…</p>;
  if (error || !order) {
    return (
      <div data-page="admin-shop-order-detail" data-state="error" className="flex flex-col gap-4">
        <Breadcrumbs items={[{ label: "Admin" }, { label: "Shop orders", to: "/admin/shop/orders" }]} />
        <p className="text-sm text-destructive" data-error>{error ?? "Order not found"}</p>
      </div>
    );
  }

  const allowedTransitions = TRANSITIONS[order.status] ?? [];
  const reasonRequired = toStatus === "cancelled" || toStatus === "failed";

  return (
    <div data-page="admin-shop-order-detail" data-state="ready" className="flex flex-col gap-6">
      <Breadcrumbs
        items={[
          { label: "Admin" },
          { label: "Shop orders", to: "/admin/shop/orders" },
          { label: `#${order.slug}` },
        ]}
      />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Order #{order.slug}</h1>
          <p className="text-sm text-muted-foreground">
            {order.provider.name} · {fmtDateTime(order.submitted_at)} ·{" "}
            {order.buyer_type === "school" ? `School buyer (${order.school_name ?? "--"})` : "Parent buyer"}
          </p>
        </div>
        <span className={`inline-flex items-center rounded px-3 py-1 text-sm font-medium uppercase ${STATUS_BADGE[order.status]}`} data-field="status">
          {order.status}
        </span>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">Items</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm" data-region="order-items">
              <thead>
                <tr className="border-b border-border text-xs uppercase text-muted-foreground">
                  <th className="py-2 text-left">Item</th>
                  <th className="py-2 text-center">Qty</th>
                  <th className="py-2 text-right">Unit</th>
                  <th className="py-2 text-right">Line</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((it) => (
                  <tr key={it.id} className="border-b border-border/60" data-item="order-line">
                    <td className="py-3">
                      <p className="font-medium">{it.name}</p>
                      <p className="text-xs text-muted-foreground">{it.sku}</p>
                    </td>
                    <td className="py-3 text-center tabular-nums">{it.quantity}</td>
                    <td className="py-3 text-right tabular-nums">{fmtMoney(it.unit_price_cents, order.currency)}</td>
                    <td className="py-3 text-right font-medium tabular-nums">{fmtMoney(it.line_total_cents, order.currency)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} className="pt-3 text-right text-sm">Subtotal (list)</td>
                  <td className="pt-3 text-right text-sm tabular-nums">{fmtMoney(order.subtotal_cents, order.currency)}</td>
                </tr>
                {order.discount_cents > 0 && (
                  <tr>
                    <td colSpan={3} className="pt-1 text-right text-sm text-primary">{order.buyer_type === "school" ? "School discount" : "Parent discount"}</td>
                    <td className="pt-1 text-right text-sm tabular-nums text-primary">−{fmtMoney(order.discount_cents, order.currency)}</td>
                  </tr>
                )}
                <tr>
                  <td colSpan={3} className="pt-3 text-right text-sm font-semibold">Total</td>
                  <td className="pt-3 text-right text-base font-semibold tabular-nums">{fmtMoney(order.total_cents, order.currency)}</td>
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>

        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Delivery</CardTitle></CardHeader>
            <CardContent className="text-sm">
              <p className="font-medium">{order.shipping_address.line1}</p>
              {order.shipping_address.line2 && <p>{order.shipping_address.line2}</p>}
              <p>{order.shipping_address.town}, {order.shipping_address.province} {order.shipping_address.postal_code}</p>
              {order.contact_phone && <p className="mt-2 text-muted-foreground">Phone: {order.contact_phone}</p>}
              {order.notes && (
                <>
                  <p className="mt-4 text-xs uppercase text-muted-foreground">Notes</p>
                  <p className="mt-1 whitespace-pre-wrap">{order.notes}</p>
                </>
              )}
            </CardContent>
          </Card>

          {allowedTransitions.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Change status</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="flex flex-col gap-3" data-form="status">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="to-status">Move to</Label>
                    <select
                      id="to-status"
                      data-field="to_status"
                      value={toStatus}
                      onChange={(e) => setToStatus(e.target.value as OrderStatus)}
                      className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                      required
                      aria-label="New status"
                    >
                      <option value="">Select…</option>
                      {allowedTransitions.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="reason">
                      Reason {reasonRequired && <span className="text-xs text-destructive">*</span>}
                    </Label>
                    <textarea
                      id="reason"
                      data-field="reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={2}
                      className="min-h-16 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      aria-label="Reason"
                      placeholder={reasonRequired ? "Required for cancel / fail" : "Optional"}
                      required={reasonRequired}
                    />
                  </div>
                  <Button type="submit" disabled={saving || toStatus === ""} data-action="save">
                    {saving ? "Saving…" : "Apply"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {order.dispatch_attempts > 0 && (
        <Card data-region="dispatch-info">
          <CardHeader><CardTitle className="text-base">API dispatch</CardTitle></CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm">
            <p>
              <span className="text-muted-foreground">Attempts:</span>{" "}
              <strong className="tabular-nums" data-field="dispatch-attempts">{order.dispatch_attempts}</strong>
            </p>
            {order.dispatch_last_attempt_at && (
              <p>
                <span className="text-muted-foreground">Last attempt:</span>{" "}
                <span className="tabular-nums">{fmtDateTime(order.dispatch_last_attempt_at)}</span>
              </p>
            )}
            {order.dispatch_last_error && (
              <p className="text-destructive" data-field="dispatch-last-error">
                <span className="text-muted-foreground">Last error:</span>{" "}
                <span className="italic">{order.dispatch_last_error}</span>
              </p>
            )}
            {(order.status === "submitted" || order.status === "failed") && (
              <div>
                <Button
                  type="button"
                  size="sm"
                  onClick={async () => {
                    try {
                      const r = await api.retryOrderDispatch(order.slug);
                      setOrder(r.order);
                    } catch (e) {
                      setError(e instanceof api.ApiError ? e.message : "Retry failed");
                    }
                  }}
                  data-action="retry-dispatch"
                >
                  Retry dispatch
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Timeline</CardTitle></CardHeader>
        <CardContent>
          <ul className="space-y-3 text-sm" data-region="status-timeline">
            <li className="flex items-baseline gap-3">
              <span className="rounded bg-muted px-2 py-0.5 text-xs uppercase text-muted-foreground">created</span>
              <span className="tabular-nums">{fmtDateTime(order.submitted_at)}</span>
              <span className="text-muted-foreground">Order submitted</span>
            </li>
            {(order.status_events ?? []).map((ev) => (
              <li key={ev.id} className="flex items-baseline gap-3" data-item="status-event">
                <span className={`rounded px-2 py-0.5 text-xs uppercase ${STATUS_BADGE[ev.to_status]}`}>{ev.to_status}</span>
                <span className="tabular-nums">{fmtDateTime(ev.created_at)}</span>
                {ev.actor_display_name && <span className="text-muted-foreground">by {ev.actor_display_name}</span>}
                {ev.reason && <span className="italic text-muted-foreground">-- {ev.reason}</span>}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
